/**
 * ENG-4936 / ENG-4929 §11.6: Slack peer-agent message classifier.
 *
 * Slack-side parity with `telegram-peer-classifier.ts`. Brings the same
 * three-layer governance gate to Slack (peer_agent_mode → peer_group_ids
 * → peer registry) plus the ENG-4935 cross-team gate path check.
 *
 * Pure functions only — no Slack API, no MCP, no global state. The
 * channel handler calls `classifyPeerMessage` after the existing ignore
 * paths (self-message, sender allowlist) and before the engagement
 * decision so bot-authored messages either ingress as peer turns or
 * drop with a labelled telemetry reason.
 *
 * Slack-specific addressing differs from Telegram in two ways:
 *
 *   1. Slack mentions arrive as `<@U…>` substrings inside `text`, not
 *      as entity offsets. We match on the literal substring because
 *      that's what Slack actually delivers in `message` / `app_mention`
 *      events. (Slack does send an `app_mention` event type that's
 *      already filtered upstream; this classifier handles the residual
 *      `message` events that carry `bot_id` from peer agents.)
 *
 *   2. Slack threads use `thread_ts`; a reply-to-this-agent signal
 *      is the thread root having been authored by us OR the parent
 *      message containing our `<@U…>`. The classifier needs only the
 *      `parent_user_id` field Slack populates on thread messages to
 *      decide reply-only addressing.
 *
 * §1.4 emergent behavior fix: pre-classifier, bot-authored Slack
 * messages were admitted whenever the inbound forward filter let them
 * through (the Pepper/Stirling cascade hot-fixed in ENG-4595 was a
 * symptom of this). Default `peer_agent_mode: 'off'` flips that to
 * deny-by-default — agents must opt in via SLACK_PEER_AGENT_MODE on
 * the per-agent slack channel config.
 */

export type SlackPeerAgentMode = 'off' | 'listen' | 'respond';

/** Subset of the Slack `message` event the classifier reads. */
export interface SlackPeerClassifierMessage {
  /** Slack message text (may contain `<@U…>` mention substrings). */
  text?: string;
  /** Slack channel id (`C…` for channels, `D…` for DMs, `G…` for legacy MPIM). */
  channel: string;
  /**
   * Channel type — Slack sends `channel_type: 'im'` for DMs. The
   * classifier treats anything else as a multi-party channel.
   */
  channel_type?: 'im' | 'mpim' | 'channel' | 'group' | string;
  /** Sender's Slack user id (the bot's user id when bot-authored). */
  user?: string;
  /** Present when the message was authored by a bot (Slack populates this). */
  bot_id?: string;
  /** Thread root ts; equal to `ts` for top-level messages. */
  thread_ts?: string;
  /** When this is a reply, Slack populates parent_user_id — the author of the thread root. */
  parent_user_id?: string;
}

/**
 * One peer agent known to this agent. Mirrors `PeerInfo` from
 * `telegram-peer-classifier.ts` — different key field (`bot_user_id`
 * instead of `bot_id`) because Slack pairs by user id, not by bot id
 * (a Slack workspace can have many bots per app).
 *
 * `gate_path` carries the ENG-4935 cross-team admission decision from
 * the manager. `null` means the manager looked but couldn't authorise
 * (revoked grant, expired, consent_required without grant) — the
 * classifier drops with `cross_team_grant_missing`. `undefined` keeps
 * the pre-ENG-4936 contract: the channel didn't ship gate metadata, so
 * we trust the SLACK_PEERS env on its own (admit).
 */
export type SlackPeerGatePath =
  | 'same_team'
  | 'intra_org_unrestricted'
  | `grant:${string}`
  | null;

export interface SlackPeerInfo {
  code_name: string;
  /** The peer's Slack user_id (`U…`). The bot's identity in chat. */
  bot_user_id: string;
  agent_id: string;
  gate_path?: SlackPeerGatePath;
}

/**
 * ENG-8669: a THIRD-PARTY bot that may WAKE this agent — Linear, Amazon Q,
 * CloudWatch, PagerDuty, GitHub.
 *
 * ## Why this is a separate registry, not another `peers` entry
 *
 * `SlackPeerInfo` is a registry of OUR OWN agents, and membership in it confers
 * conversational-peer privileges: an admitted peer is someone this agent can
 * reply to. A third-party bot must never inherit that. If Linear's bot were
 * added to `peers`, the agent could answer it — and two bots talking to each
 * other in a customer's channel is precisely the emergent behaviour ENG-4936
 * exists to prevent.
 *
 * So the two are different types producing different classification kinds, and
 * the distinction is carried in the type system rather than in a comment
 * somebody has to remember. A `bot-ingress` result has no `SlackPeerInfo` on it
 * at all, so there is nothing for a reply path to address even by mistake.
 *
 * ## Why it is keyed per bot AND per channel
 *
 * "Admit all bots" is not offered. An allowlist that is easy to over-grant gets
 * over-granted, and the blast radius here is an agent that can be woken by
 * anything that can post — including anything a third party can make post.
 * Every entry names one bot and the channels it may wake this agent in.
 */
export interface SlackThirdPartyBotEntry {
  /** The bot's Slack user id (`U…`) — the same key `peers` uses. */
  bot_user_id: string;
  /**
   * ENG-8669 follow-up: the app's `B…` bot id, as it appears on the event.
   *
   * Required in practice for webhook-posting apps. Linear, PagerDuty and
   * CloudWatch post with a `bot_id` and NO `user`, so an entry keyed only on
   * `bot_user_id` can never match — `msg.user` is `undefined` and the compare
   * silently fails. That is what made ENG-8669 inert for the very integrations
   * it named.
   *
   * Kept as a distinct field rather than overloading `bot_user_id` so an
   * operator is never guessing which id shape a field named "user id" wants,
   * and so a wrong guess is a visible mismatch rather than silence.
   */
  bot_id?: string;
  /**
   * Channels where THIS bot may wake THIS agent. Never empty in practice: an
   * empty list admits nowhere, which is the safe reading of "no channels were
   * named" and is asserted in the tests.
   */
  channel_ids: ReadonlyArray<string>;
  /** Operator-facing label for telemetry (e.g. "linear", "amazon-q"). */
  label?: string;
}

export interface SlackPeerClassifierConfig {
  peer_agent_mode: SlackPeerAgentMode;
  /** Slack channel ids where peer-agent behaviour is enabled. Empty = DMs only. */
  peer_group_ids: ReadonlyArray<string>;
  /** Peers from the agent's CHARTER `multi_agent.slack_peers`. */
  peers: ReadonlyArray<SlackPeerInfo>;
  /**
   * ENG-4940 / ENG-4929 §8: channel-agnostic peer kill switch. Same
   * semantics as the Telegram classifier — 'all' short-circuits every
   * bot-authored message; 'cross_team_only' drops peers whose resolved
   * gate_path is not 'same_team'.
   */
  peer_disabled_mode?: 'off' | 'cross_team_only' | 'all';
  /**
   * ENG-8669: third-party bots allowed to WAKE this agent, per channel.
   * Absent/empty preserves the pre-ENG-8669 behaviour exactly — every
   * non-peer bot drops as `unknown_peer`.
   */
  third_party_bots?: ReadonlyArray<SlackThirdPartyBotEntry>;
}

export interface SlackPeerClassifierSelf {
  /** Our own Slack user_id (`U…`). Set via auth.test at startup. */
  bot_user_id: string | null;
}

export type SlackPeerDropReason =
  | 'mode_off'
  | 'chat_not_allowed'
  | 'unknown_peer'
  | 'not_addressed'
  | 'self_resolution_pending'
  | 'cross_team_grant_missing'
  /** ENG-4940 / §8: kill switch is 'all'. */
  | 'peer_disabled_all'
  /** ENG-4940 / §8: kill switch is 'cross_team_only', this peer is cross-team. */
  | 'peer_disabled_cross_team'
  /**
   * ENG-8669 / AC5: this bot IS allowlisted, but not for this channel — so an
   * allowlist entry WOULD have admitted it elsewhere. Distinct from
   * `unknown_peer` on purpose: "you nearly configured this correctly" and "this
   * sender is a stranger" need different answers from an operator, and today an
   * `unknown_peer` drop is indistinguishable from the message never arriving,
   * which is why this class of gap took a day to characterise.
   */
  | 'third_party_channel_not_allowlisted';

export type SlackPeerClassification =
  | { kind: 'human' }
  | { kind: 'self' }
  | { kind: 'drop'; reason: SlackPeerDropReason }
  | { kind: 'peer-ingress'; peer: SlackPeerInfo }
  /**
   * ENG-8669: an allowlisted third-party bot woke us. INGRESS-ONLY.
   *
   * Deliberately carries `bot`, never `peer`. A reply path takes a
   * `SlackPeerInfo`, so there is physically nothing here to reply to — the
   * ingress-only guarantee is a property of the type, not of a convention
   * someone has to honour. See `isReplyEligible`.
   */
  | { kind: 'bot-ingress'; bot: SlackThirdPartyBotEntry };

const CODE_NAME_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Match a bot-authored message against the third-party allowlist.
 *
 * ENG-8669 follow-up: match on the bot id as well as the user id. A
 * webhook-posting app (Linear, PagerDuty, CloudWatch — the three this feature
 * was built for) sends `bot_id` and no `user`, so the original
 * `bot_user_id === msg.user` compared against `undefined` and never matched.
 * Both ids are accepted on both fields because an operator who pastes the `B…`
 * they can actually see should not be silently ignored.
 */
function findThirdPartyGrant(
  msg: SlackPeerClassifierMessage,
  cfg: SlackPeerClassifierConfig,
): SlackThirdPartyBotEntry | undefined {
  return cfg.third_party_bots?.find((b) => {
    if (msg.user && b.bot_user_id === msg.user) return true;
    if (!msg.bot_id) return false;
    return b.bot_id === msg.bot_id || b.bot_user_id === msg.bot_id;
  });
}

export function classifyPeerMessage(
  msg: SlackPeerClassifierMessage,
  cfg: SlackPeerClassifierConfig,
  self: SlackPeerClassifierSelf,
): SlackPeerClassification {
  // Human path is unchanged — `bot_id` is Slack's authoritative signal
  // that a message was authored by a bot. Absent → treat as human, even
  // if `user` happens to be a bot user id (Slack always populates bot_id
  // when relevant).
  if (!msg.bot_id) return { kind: 'human' };

  // Defensive: getEvents shouldn't echo our own outbound messages, but
  // if it ever did we must not loop back into ingress.
  if (self.bot_user_id !== null && msg.user === self.bot_user_id) {
    return { kind: 'self' };
  }

  // ENG-4940 / §8: kill switch — 'all' short-circuits every bot
  // message before any other gate. 'cross_team_only' is checked further
  // down after peer resolution so we can read gate_path.
  if (cfg.peer_disabled_mode === 'all') {
    return { kind: 'drop', reason: 'peer_disabled_all' };
  }

  // ENG-10027: the third-party grant is its OWN opt-in axis and is resolved
  // here, BEFORE the two peer-collaboration gates below.
  //
  // The previous placement (after `mode_off` and `chat_not_allowed`) was
  // deliberate and argued for in the comment that used to live further down —
  // "every safety leaf an operator can reach for still fires first". The
  // reasoning was careful and the effect was that the allowlist could never be
  // reached by the population it was built for. `parsePeerAgentModeEnv(undefined)`
  // returns 'off', and an agent with no peer collaboration configured has no
  // SLACK_PEER_AGENT_MODE in its env at all — so a Linear card hit `mode_off`
  // two statements early, and `chat_not_allowed` (empty peer_group_ids, non-im
  // channel) would have caught it next. Two independent blockers, neither
  // having anything to do with third-party bots. sherlock established this on
  // the first live AC6 run, 2026-09-05, and the prediction it made was then
  // confirmed: with the peer settings forced on, a Linear card DID wake the
  // agent.
  //
  // Why those two gates must not apply, rather than merely being inconvenient:
  //   - `peer_agent_mode` governs AGENT-TO-AGENT collaboration. A Linear
  //     webhook is not a peer agent and will never be one. Requiring an
  //     operator to enable peer collaboration in order to receive an alert card
  //     makes an unrelated setting a precondition.
  //   - `peer_group_ids` is redundant here by construction: a grant NAMES the
  //     channels it may wake us in, and that list is strictly more specific.
  //     Requiring both means the narrower list is silently ANDed with a broader
  //     one that was configured for something else.
  //   - An operator who adds a bot to the allowlist WITH a channel list has
  //     opted in more explicitly than one who sets a mode to 'listen'. Reading
  //     that as "not opted in" inverts the intent.
  //
  // The KILL SWITCHES still apply and are still checked first: `all` above,
  // and `cross_team_only` inside the branch. AC4 is satisfied — the allowlist
  // relaxes `unknown_peer` and `not_addressed`, and now also the two peer
  // preconditions that were never about it, and nothing else.
  //
  // Peers keep priority: this only runs when the sender is NOT a declared peer,
  // so a real peer is still classified as a peer on the path below, and a bot
  // that is neither still drops with its original reason and ordering.
  if (!cfg.peers.some((p) => p.bot_user_id === msg.user)) {
    const grant = findThirdPartyGrant(msg, cfg);
    if (grant) {
      // The 'cross_team_only' kill switch, checked here because a third-party
      // bot never resolves a gate_path. It has no team; it is not same-team,
      // it is not our org at all. An operator narrowing trust to same-team
      // peers has not made an exception for Linear.
      if (cfg.peer_disabled_mode === 'cross_team_only') {
        return { kind: 'drop', reason: 'peer_disabled_cross_team' };
      }
      // Per-channel, always. An empty list admits nowhere — "no channels were
      // named" reads as no grant, never as a blanket one.
      if (grant.channel_ids.includes(msg.channel)) {
        return { kind: 'bot-ingress', bot: grant };
      }
      // AC5: the "would have been admitted" signal. Same bot, wrong room — a
      // different fix from "add the bot".
      return { kind: 'drop', reason: 'third_party_channel_not_allowlisted' };
    }
  }

  // §6.3.1 layer 1 — peer mode opt-in.
  if (cfg.peer_agent_mode === 'off') {
    return { kind: 'drop', reason: 'mode_off' };
  }

  // §6.3.1 layer 2 — chat scope. Empty peer_group_ids = DMs only.
  if (cfg.peer_group_ids.length === 0) {
    if (msg.channel_type !== 'im') {
      return { kind: 'drop', reason: 'chat_not_allowed' };
    }
  } else if (!cfg.peer_group_ids.includes(msg.channel)) {
    return { kind: 'drop', reason: 'chat_not_allowed' };
  }

  // §6.3.1 layer 3 — peer registry, keyed on Slack user_id (the
  // immutable per-workspace bot identity).
  const peer = cfg.peers.find((p) => p.bot_user_id === msg.user);
  if (!peer) {
    // ENG-10027: the third-party allowlist used to be consulted HERE. It is now
    // resolved above, before the mode and chat-scope gates, because it is not a
    // peer-collaboration setting and must not inherit peer-collaboration
    // preconditions. Reaching this line therefore means the sender is neither a
    // declared peer nor an allowlisted third-party bot — a genuine stranger.
    return { kind: 'drop', reason: 'unknown_peer' };
  }

  // ENG-4935 cross-team gate. `null` means the manager looked but
  // couldn't authorise (revoked grant / expired / consent_required
  // without grant). `undefined` is the pre-wiring backwards-compat
  // path (admit, matches today's Slack behaviour before this PR).
  if (peer.gate_path === null) {
    return { kind: 'drop', reason: 'cross_team_grant_missing' };
  }

  // ENG-4940 / §8: kill switch 'cross_team_only' — same-team peers
  // admit, cross-team peers drop. `undefined` is treated as same-team
  // for backwards compat (older manager hasn't wired gates yet).
  if (
    cfg.peer_disabled_mode === 'cross_team_only' &&
    peer.gate_path !== undefined &&
    peer.gate_path !== 'same_team'
  ) {
    return { kind: 'drop', reason: 'peer_disabled_cross_team' };
  }

  // Addressing matrix needs our own identity. If auth.test hasn't
  // resolved we can't safely admit — surface as a distinct telemetry
  // tag so ops can see the silent-period instead of wondering why a
  // peer never got through.
  if (self.bot_user_id === null) {
    return { kind: 'drop', reason: 'self_resolution_pending' };
  }

  const addressing = classifyAddressing(msg, self.bot_user_id);
  if (!addressing.addressed) {
    return { kind: 'drop', reason: 'not_addressed' };
  }

  // Same ENG-4909 §5.5 #5 reply-only carve-out as Telegram: a thread
  // reply alone is not sufficient. Require either an explicit mention
  // entity for us OR the chat to be in our peer_group_ids AND mode =
  // 'respond' (not 'listen'). DMs (peer_group_ids empty) need an
  // explicit mention.
  if (addressing.viaReplyOnly) {
    const inAllowedGroup =
      cfg.peer_group_ids.length > 0 && cfg.peer_group_ids.includes(msg.channel);
    const respondMode = cfg.peer_agent_mode === 'respond';
    if (!(inAllowedGroup && respondMode)) {
      return { kind: 'drop', reason: 'not_addressed' };
    }
  }
  return { kind: 'peer-ingress', peer };
}

/**
 * ENG-8669 AC3: may this agent send a channel reply ADDRESSED TO the sender?
 *
 * Only a peer agent. An allowlisted third-party bot woke us so we could act -
 * read the Linear issue, look at the alarm - not so we could talk to it. Two
 * bots holding a conversation in a customer's channel is the emergent behaviour
 * the peer classifier was built to prevent, and an allowlist that reintroduced
 * it would be a straight regression of ENG-4936.
 *
 * Exported as a predicate rather than left to each call site's judgement,
 * because "remember not to reply to those" is a convention and conventions rot.
 * The type system does most of the work already - a `bot-ingress` carries no
 * `SlackPeerInfo`, so there is no peer identity to address - and this is the
 * explicit statement of the same rule for code that only has the kind.
 */
export function isReplyEligible(classification: SlackPeerClassification): boolean {
  return classification.kind === 'peer-ingress' || classification.kind === 'human';
}

/**
 * ENG-8669 AC5: the telemetry label for an admitted ingress, so an admit that
 * came VIA the third-party allowlist is distinguishable from an ordinary peer
 * admit in the same stream.
 */
export function ingressSource(
  classification: SlackPeerClassification,
): 'peer' | 'third-party-bot' | null {
  if (classification.kind === 'peer-ingress') return 'peer';
  if (classification.kind === 'bot-ingress') return 'third-party-bot';
  return null;
}

export interface SlackAddressingResult {
  addressed: boolean;
  /** True only when reply-to-our-thread was the only trigger. */
  viaReplyOnly: boolean;
}

/**
 * Slack §4 addressing — three triggers, same shape as Telegram:
 *
 *   1. Literal `<@OURID>` mention substring in the message text.
 *   2. Thread reply where `parent_user_id` is our bot_user_id (the
 *      thread root was authored by us).
 *   3. The message text starts with `<@OURID>` — treated identically
 *      to #1; broken out only so future hooks can distinguish leading
 *      addressed messages from in-line mentions.
 *
 * Plain `@username` text without the bracketed user-id form does *not*
 * count — Slack only renders the `<@U…>` form when the user actually
 * picked our handle from the autocompleter, which matches the §2.3
 * "entity, not substring" requirement.
 */
export function classifyAddressing(
  msg: SlackPeerClassifierMessage,
  ourBotUserId: string,
): SlackAddressingResult {
  const text = msg.text ?? '';
  const mentionToken = `<@${ourBotUserId}>`;
  const viaMention = text.includes(mentionToken);
  const viaReply =
    !!msg.thread_ts &&
    msg.thread_ts !== undefined &&
    msg.parent_user_id === ourBotUserId;

  if (viaMention) {
    return { addressed: true, viaReplyOnly: false };
  }
  if (viaReply) {
    return { addressed: true, viaReplyOnly: true };
  }
  return { addressed: false, viaReplyOnly: false };
}

/**
 * Parse the `SLACK_PEERS` env var (a JSON array of
 * `{ code_name, bot_user_id, agent_id }` triples). Invalid entries are
 * skipped silently rather than failing startup. ENG-4935 gate paths
 * are folded in from the `SLACK_PEERS_GATE` companion env (a JSON
 * `{ bot_user_id: gate_path }` map). A present-but-malformed gate env
 * fails closed: every peer's gate_path becomes `null` and the
 * classifier drops with `cross_team_grant_missing`.
 */
export function parsePeersEnv(
  raw: string | undefined,
  gateRaw?: string | undefined,
): SlackPeerInfo[] {
  if (!raw || raw.trim().length === 0) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const gateMap = new Map<string, SlackPeerGatePath>();
  let gateConfigInvalid = false;
  // ENG-7786: whether a gate env was supplied at all. When present, a
  // registered peer MISSING from the map fails closed (below). When absent,
  // gate_path stays undefined and the classifier admits (pre-wiring compat).
  const gatePresent = !!(gateRaw && gateRaw.trim().length > 0);
  if (gatePresent) {
    try {
      const gateParsed = JSON.parse(gateRaw);
      if (!gateParsed || typeof gateParsed !== 'object' || Array.isArray(gateParsed)) {
        gateConfigInvalid = true;
      } else {
        for (const [k, v] of Object.entries(gateParsed as Record<string, unknown>)) {
          if (v === null) {
            gateMap.set(k, null);
          } else if (typeof v === 'string') {
            const isGrantPath =
              v.startsWith('grant:') && v.slice('grant:'.length).trim().length > 0;
            if (v === 'same_team' || v === 'intra_org_unrestricted' || isGrantPath) {
              gateMap.set(k, v as SlackPeerGatePath);
            } else {
              gateConfigInvalid = true;
              break;
            }
          } else {
            gateConfigInvalid = true;
            break;
          }
        }
      }
    } catch {
      gateConfigInvalid = true;
    }
    if (gateConfigInvalid) {
      // stderr matches the rest of MCP boot logging so the operator
      // notices the misconfiguration without grepping traffic logs.
      console.error(
        '[slack-peer-classifier] SLACK_PEERS_GATE is present but malformed; failing closed (every peer drops as cross_team_grant_missing)',
      );
    }
  }

  const out: SlackPeerInfo[] = [];
  for (const entry of parsed) {
    if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as { code_name?: unknown }).code_name === 'string' &&
      CODE_NAME_RE.test((entry as { code_name: string }).code_name) &&
      typeof (entry as { agent_id?: unknown }).agent_id === 'string' &&
      typeof (entry as { bot_user_id?: unknown }).bot_user_id === 'string' &&
      (entry as { bot_user_id: string }).bot_user_id.length > 0
    ) {
      const e = entry as { code_name: string; agent_id: string; bot_user_id: string };
      const peer: SlackPeerInfo = {
        code_name: e.code_name,
        bot_user_id: e.bot_user_id,
        agent_id: e.agent_id,
      };
      if (gateConfigInvalid) {
        peer.gate_path = null;
      } else if (gateMap.has(e.bot_user_id)) {
        peer.gate_path = gateMap.get(e.bot_user_id) ?? null;
      } else if (gatePresent) {
        // ENG-7786: a present, valid gate map that OMITS this registered peer
        // must fail closed - drop as cross_team_grant_missing rather than
        // admitting via the pre-wiring `undefined` fallback. `undefined` is
        // reserved for "no gate env at all" (older manager, backwards compat).
        peer.gate_path = null;
      }
      out.push(peer);
    }
  }
  return out;
}

export function parsePeerGroupIdsEnv(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * ENG-8669: parse `SLACK_THIRD_PARTY_BOTS` into the allowlist.
 *
 * Format is JSON, not the comma-separated shape the sibling parsers use,
 * because an entry is a bot PLUS its channels and a flat list cannot express
 * that pairing without inventing a delimiter nobody will remember:
 *
 *   [{"bot_user_id":"U123","channel_ids":["C_TRIAGE"],"label":"linear"}]
 *
 * Every parse failure yields `[]` — the closed state. This registry decides who
 * may wake an agent, so a malformed value must never be interpreted
 * generously: half a list is worse than none, because it silently grants
 * whatever happened to parse.
 *
 * Entries missing `bot_user_id` or `channel_ids` are dropped individually
 * rather than failing the whole list, so one bad line does not silently disable
 * a working allowlist. Both behaviours are asserted in the tests.
 */
export function parseThirdPartyBotsEnv(
  raw: string | undefined,
): SlackThirdPartyBotEntry[] {
  if (!raw || !raw.trim()) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const out: SlackThirdPartyBotEntry[] = [];
  for (const item of parsed) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const botUserId = typeof rec['bot_user_id'] === 'string' ? rec['bot_user_id'].trim() : '';
    if (!botUserId) continue;
    // Raised in review: an ABSENT `channel_ids` is a malformed entry, not a
    // deliberate grant to nowhere. Retaining it as `[]` quietly changed that
    // bot's telemetry from `unknown_peer` to
    // `third_party_channel_not_allowlisted` - i.e. a typo started reporting as
    // "you nearly configured this correctly", which is the opposite of the
    // legibility AC5 asks for.
    //
    // An EXPLICIT `[]` stays valid: that is someone deliberately writing down a
    // bot with no channels yet, and it should read as the grant-to-nowhere it is.
    if (!Array.isArray(rec['channel_ids'])) continue;
    const channels = rec['channel_ids']
      .filter((c): c is string => typeof c === 'string' && c.trim() !== '')
      .map((c) => c.trim());
    const label = typeof rec['label'] === 'string' && rec['label'].trim() ? rec['label'].trim() : undefined;
    // ENG-8669 follow-up: optional `B…` bot id. Optional rather than required
    // so existing configured entries keep parsing unchanged.
    const botId = typeof rec['bot_id'] === 'string' && rec['bot_id'].trim() ? rec['bot_id'].trim() : undefined;
    out.push({
      bot_user_id: botUserId,
      channel_ids: channels,
      ...(botId ? { bot_id: botId } : {}),
      ...(label ? { label } : {}),
    });
  }
  return out;
}

export function parsePeerAgentModeEnv(raw: string | undefined): SlackPeerAgentMode {
  if (raw === 'listen' || raw === 'respond') return raw;
  return 'off';
}
